import { createContext, useContext, type ReactNode } from 'react';

import { VIEWPORT_SCREEN } from './AppViewport';
import { FeatItIcon } from './FeatItIcon';
import { FeatItWordmark } from './FeatItWordmark';

/**
 * The one frame of every page a person meets before an account, or while connecting a client: sign-in,
 * sign-up, the password and email pages, setup and claim, an invitation, the desktop's first screens,
 * the CLI, phone and assistant approvals, the passkey and app-setup pages, a reporter's page (I-0899).
 *
 * It draws what those pages share, so none of them draws its own: the canvas, the column, the FEATit
 * mark, and the page's heading under it. A page's content goes in an `AuthCard`. Two families had
 * grown apart — sign-in's centred mark over a borderless card, and the approvals' bordered card with
 * no mark at all, one of them with no dark theme — at six different widths
 * (`auth-frame.spec.ts` measured them). Every route under `PublicLayout` renders inside this
 * (`authFrame.guard.test.ts`).
 */

/** The column: a form or a message, an approval or a setup step, a reporter's own page. */
const WIDTHS = {
  form: 'max-w-[360px]',
  panel: 'max-w-[440px]',
  page: 'max-w-[640px]',
} as const;

export type AuthFrameWidthType = keyof typeof WIDTHS;

/** A page's heading, under the mark, and the sentence under it. */
export const AUTH_TITLE_CLASS =
  'mt-4 text-[17px] font-semibold tracking-tight text-gray-900 dark:text-gray-100';
export const AUTH_SUBTITLE_CLASS = 'mt-1 text-[13px] text-gray-500 dark:text-gray-400';

/** The surface a page's content sits on. `auth-card` names it to an embedding frame. */
export const AUTH_CARD_CLASS = 'auth-card rounded-xl bg-white p-6 shadow-sm dark:bg-[#171717]';

/** T-1144: the sign-in page's softer card, its corners rounder than the frame's plain one. */
export const AUTH_WELCOME_CARD_CLASS = AUTH_CARD_CLASS.replace('rounded-xl', 'rounded-2xl');

/**
 * F-0581 OB-3: a page drawn inside another frame — Get started's step 2 draws the sign-in, sign-up
 * and setup pages themselves, so a fix to one is a fix to both. Inside, `AuthFrame` draws only the
 * page's content: no screen, no mark, no heading (the step has its own), and its card lies flat on
 * the step's card.
 */
export const AuthFrameEmbedContext = createContext(false);

const EMBEDDED_CARD =
  '[&_.auth-card]:rounded-none [&_.auth-card]:bg-transparent [&_.auth-card]:p-0 [&_.auth-card]:shadow-none dark:[&_.auth-card]:bg-transparent';

export function AuthFrame({
  width = 'form',
  title,
  subtitle,
  before,
  busy,
  firstScreen = false,
  welcoming = false,
  brandBar,
  children,
}: {
  width?: AuthFrameWidthType;
  /**
   * The page's heading. Omitted where the card names what is being approved in its own heading;
   * `null` while the page is still deciding it, which keeps the heading's line so nothing below moves
   * when it arrives.
   */
  title?: ReactNode | null;
  subtitle?: ReactNode;
  /** Above the mark: the desktop window's way back to its first screen. */
  before?: ReactNode;
  /** While the page is still reading what it shows. */
  busy?: boolean;
  /**
   * The desktop app's first screen (F-0518): the mark larger, the column centred in the window, the
   * product named under it. Every other page opens on a task and keeps the standard header.
   */
  firstScreen?: boolean;
  /** T-1144: a soft glow in the mark's own colours behind it, on the page that greets someone. */
  welcoming?: boolean;
  /**
   * F-0581 OB-15 (T-0684): a page of its own rather than a form — Get started — draws the mark and
   * the wordmark in a bar at the top-left, with this at its right, in place of the centred mark.
   * Every other page leaves it out and keeps the centred mark.
   */
  brandBar?: { end?: ReactNode };
  children?: ReactNode;
}) {
  const embedded = useContext(AuthFrameEmbedContext);
  if (embedded) {
    return (
      <div data-auth-embedded="" aria-busy={busy || undefined} className={EMBEDDED_CARD}>
        {children}
      </div>
    );
  }
  if (brandBar) {
    return (
      <div
        data-auth-frame
        className={`${VIEWPORT_SCREEN} flex flex-col items-center bg-gray-50 px-4 pb-12 pt-6 sm:pt-7 dark:bg-[#212121]`}
      >
        <div
          data-auth-brand-bar=""
          className="mb-7 flex w-full max-w-[1120px] flex-wrap items-center justify-between gap-3"
        >
          <span className="inline-flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <FeatItIcon className="h-[22px] w-[22px]" />
            <FeatItWordmark className="h-[13px] w-auto" />
          </span>
          {brandBar.end}
        </div>
        <div className={`w-full ${WIDTHS[width]}`} aria-busy={busy || undefined}>
          {before}
          {children}
        </div>
      </div>
    );
  }
  return (
    <div
      data-auth-frame
      className={`${VIEWPORT_SCREEN} flex justify-center bg-gray-50 px-4 py-12 dark:bg-[#212121]`}
    >
      <div
        className={`w-full ${WIDTHS[width]}${firstScreen ? ' flex flex-col justify-center' : ''}`}
        aria-busy={busy || undefined}
      >
        {before}
        <div
          className={`${firstScreen ? 'mb-8 text-center' : 'mb-6 text-center'}${welcoming ? ' relative isolate' : ''}`}
        >
          {welcoming ? (
            <span
              data-auth-glow=""
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-[26px] -z-10 h-[200px] w-[200px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(245,180,70,0.10),rgba(70,130,220,0.05)_55%,transparent)] dark:bg-[radial-gradient(closest-side,rgba(245,180,70,0.20),rgba(70,130,220,0.10)_55%,transparent)]"
            />
          ) : null}
          <FeatItIcon
            className={`mx-auto object-contain ${firstScreen ? 'h-[64px] w-[64px]' : 'h-[53px] w-[53px]'}`}
          />
          {title === null ? (
            <p aria-hidden="true" className={`invisible ${AUTH_TITLE_CLASS}`}>
              &nbsp;
            </p>
          ) : title ? (
            <h1 className={firstScreen ? 'mt-5' : AUTH_TITLE_CLASS}>{title}</h1>
          ) : null}
          {subtitle ? (
            <p
              className={
                firstScreen
                  ? 'mx-auto mt-3 max-w-[300px] text-[13px] leading-5 text-gray-500 dark:text-gray-400'
                  : AUTH_SUBTITLE_CLASS
              }
            >
              {subtitle}
            </p>
          ) : null}
        </div>
        {children}
      </div>
    </div>
  );
}

/** A page's content, on the frame's one card. */
export function AuthCard({
  children,
  className,
  ...rest
}: {
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}) {
  return (
    <div className={`${AUTH_CARD_CLASS}${className ? ` ${className}` : ''}`} {...rest}>
      {children}
    </div>
  );
}

/**
 * The heading of an approval — the CLI, the phone app, an assistant, a passkey window: what is asking,
 * as a tile, and what it asks. It sits at the top of the card, under the frame's mark, so each
 * approval names its client in one place and in one way.
 */
export function AuthApprovalHeading({
  icon,
  image,
  title,
  detail,
}: {
  /** A glyph, drawn on the approval's tile. */
  icon?: ReactNode;
  /** Or a picture that is its own tile: a deployment's icon. */
  image?: ReactNode;
  title: ReactNode;
  detail?: ReactNode;
}) {
  return (
    <div className="mb-5 flex items-center gap-3">
      {image ?? (
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900">
          {icon}
        </span>
      )}
      <div className="min-w-0">
        <h1 className="text-[17px] font-semibold tracking-tight text-gray-900 dark:text-gray-100">
          {title}
        </h1>
        {detail ? (
          <p className="mt-0.5 text-[13px] text-gray-500 [overflow-wrap:anywhere] dark:text-gray-400">
            {detail}
          </p>
        ) : null}
      </div>
    </div>
  );
}
